import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Store as StoreIcon,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ShieldCheck,
  ShieldAlert,
  Wallet,
  Coins,
  Package,
  Boxes,
  ShoppingCart,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Ban,
  Eye,
  ArrowRight,
  ArrowUpRight,
  ArrowDownLeft,
  IndianRupee,
  RefreshCw,
  PlusCircle,
  Layers,
  Star,
  Truck,
  Receipt,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StandardImage } from "@/components/shared";
import {
  useMyStore,
  useOrderById,
  useStoreDashboard,
  useStoreId,
  useStoreLowStock,
  useStoreOrders,
} from "@/hooks/queries";
import { channelOrdersService, type ChannelOrder } from "@/services/channelOrdersService";
import { channelStockService } from "@/services/channelStockService";
import { useAuth } from "@/contexts/AuthContext";
import { useMyWallet } from "@/hooks/queries/useWallets";
import type { StoreOrderRow } from "@/types/store.ui";
import { mapOrderStatusToStoreUi } from "@/lib/mappers/store-mappers";

const customerStatusBadgeColor: Record<string, string> = {
  new: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  confirmed: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  preparing: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
  ready: "bg-primary/10 text-primary border-primary/20",
  dispatched: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

function formatInr(amount: number) {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function getChannelStatusBadge(statusText: string) {
  switch (statusText?.toLowerCase()) {
    case "approved":
      return (
        <Badge
          variant="outline"
          className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 text-xs gap-1 font-semibold"
        >
          <CheckCircle2 className="h-3 w-3" />
          Approved
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          variant="outline"
          className="bg-destructive/10 text-destructive border-destructive/20 text-xs gap-1 font-semibold"
        >
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    case "cancelled":
      return (
        <Badge
          variant="outline"
          className="bg-muted text-muted-foreground border-border text-xs gap-1 font-semibold"
        >
          <Ban className="h-3 w-3" />
          Cancelled
        </Badge>
      );
    case "pending":
    default:
      return (
        <Badge
          variant="outline"
          className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs gap-1 font-semibold"
        >
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
  }
}

const StoreDashboard = () => {
  const { user } = useAuth();
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: myStore, isLoading: myStoreLoading } = useMyStore(Boolean(storeId));
  const { data: stats, isLoading: statsLoading } = useStoreDashboard(storeId);
  const { data: ordersData, isLoading: ordersLoading } = useStoreOrders(storeId);
  const { data: lowStockProducts = [], isLoading: lowStockLoading } = useStoreLowStock(storeId);
  const wallet = useMyWallet();

  // Supply purchase orders placed with wholesale channel partner
  const supplyOrders = useQuery({
    queryKey: ["channel", "orders", "incoming"],
    queryFn: () => channelOrdersService.incoming(),
  });

  // Channel supply stock on hand
  const channelStock = useQuery({
    queryKey: ["channel", "stock"],
    queryFn: () => channelStockService.getMine(),
  });

  // Dialog state for customer orders
  const [selectedCustomerOrderId, setSelectedCustomerOrderId] = useState<number | null>(null);
  const { data: selectedCustomerOrder, isLoading: customerOrderDetailLoading } = useOrderById(
    selectedCustomerOrderId ?? 0,
    selectedCustomerOrderId !== null,
  );

  // Dialog state for channel supply orders
  const [selectedSupplyOrder, setSelectedSupplyOrder] = useState<ChannelOrder | null>(null);

  const isLoading =
    storeLoading ||
    myStoreLoading ||
    statsLoading ||
    ordersLoading ||
    lowStockLoading ||
    supplyOrders.isLoading ||
    channelStock.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-44 w-full rounded-2xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (!storeId || !stats || !myStore) {
    return (
      <div className="space-y-4 py-8 text-center">
        <StoreIcon className="mx-auto h-12 w-12 text-muted-foreground/60" />
        <h2 className="text-xl font-bold">No Store Linked</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          No store has been configured or linked to your account. If you believe this is an error, please reach out to admin support.
        </p>
      </div>
    );
  }

  const customerOrders: StoreOrderRow[] = ordersData?.items ?? [];
  const supplyOrdersList: ChannelOrder[] = supplyOrders.data ?? [];
  const channelStockList = channelStock.data ?? [];

  // Metrics calculations
  const totalSupplyOrders = supplyOrdersList.length;
  const pendingSupplyCount = supplyOrdersList.filter((o) => o.statusText === "Pending").length;
  const completedSupplyCount = supplyOrdersList.filter((o) => o.statusText === "Approved").length;

  const totalChannelUnits = channelStockList.reduce((sum, r) => sum + r.quantity, 0);

  const activeCustomerOrders = customerOrders.filter(
    (o) => !["delivered", "cancelled"].includes(o.status),
  );
  const deliveredCustomerOrders = customerOrders.filter((o) => o.status === "delivered");

  const totalAllOrders = stats.totalOrders + totalSupplyOrders;
  const totalPendingOrders = activeCustomerOrders.length + pendingSupplyCount;
  const totalCompletedOrders = deliveredCustomerOrders.length + completedSupplyCount;

  // Profile fields from existing models
  const storeName = myStore.storeName || "My Retail Store";
  const ownerName = user?.name || myStore.ownerName || "Store Owner";
  const storeUid = `#STR-${myStore.id}`;
  const phone = myStore.phone || user?.phone || "—";
  const email = myStore.email || user?.email || "—";
  const addressParts = [myStore.fullAddress, myStore.city, myStore.state, myStore.pincode].filter(
    Boolean,
  );
  const fullAddress = addressParts.length > 0 ? addressParts.join(", ") : myStore.city || "—";
  const joinDate = formatDate(myStore.createdAt);
  const accountStatus = myStore.statusText || (myStore.isOpen ? "Open & Active" : "Closed");
  const kycStatus = myStore.kycVerified;

  return (
    <div className="space-y-6">
      {/* 1. STORE PROFILE / BUSINESS INFORMATION CARD */}
      <Card className="overflow-hidden border-border/60 bg-card shadow-sm">
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 sm:p-7 border-b border-border/40">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start sm:items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md shadow-primary/20">
                <StoreIcon className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                    {storeName}
                  </h1>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 font-semibold">
                    Retail Store
                  </Badge>
                  <Badge
                    variant="outline"
                    className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 font-medium"
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    {accountStatus}
                  </Badge>
                  {kycStatus ? (
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-xs">
                      KYC Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-xs">
                      KYC Pending
                    </Badge>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Retail merchant store operations • Customer orders & wholesale inventory supply.
                </p>
                {myStore.deliveryRadius > 0 && (
                  <p className="text-xs font-medium text-primary">
                    Delivery Radius: {myStore.deliveryRadius} km • Free Delivery above ₹{myStore.freeDeliveryAbove || 0}
                  </p>
                )}
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-wrap items-center gap-2.5 pt-2 lg:pt-0">
              <Button asChild className="gap-2 shadow-sm font-semibold">
                <Link to="/store/orders">
                  <ShoppingCart className="h-4 w-4" />
                  Store Orders
                </Link>
              </Button>
              <Button asChild variant="outline" className="gap-2">
                <Link to="/store/supply">
                  <Boxes className="h-4 w-4" />
                  Order Supply
                </Link>
              </Button>
              <Button asChild variant="outline" className="gap-2">
                <Link to="/store/products">
                  <Package className="h-4 w-4" />
                  Products
                </Link>
              </Button>
              <Button asChild variant="secondary" className="gap-2">
                <Link to="/store/earnings">
                  <Wallet className="h-4 w-4 text-primary" />
                  Earnings
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Details Grid */}
        <CardContent className="p-5 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-border/40">
            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0 first:pl-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                Store Owner
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={ownerName}>
                {ownerName}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                Store ID
              </span>
              <p className="text-sm font-semibold text-foreground font-mono truncate" title={storeUid}>
                {storeUid}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Mobile Number
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={phone}>
                {phone}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                Email Address
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={email}>
                {email}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                Store Address
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={fullAddress}>
                {fullAddress}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0 last:pr-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                Registered On
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={joinDate}>
                {joinDate}
              </p>
            </div>
          </div>

          {/* Quick Balance & Rating Banner */}
          <div className="mt-5 pt-4 border-t border-border/50 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-emerald-700 dark:text-emerald-400 font-medium">
                <IndianRupee className="h-4 w-4" />
                <span>Total Earnings:</span>
                <strong className="text-base font-bold">{formatInr(stats.totalEarnings)}</strong>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-1.5 text-amber-700 dark:text-amber-400 font-medium">
                <Clock className="h-4 w-4" />
                <span>Pending Settlement:</span>
                <strong className="text-base font-bold">{formatInr(stats.pendingSettlement)}</strong>
              </div>
              {wallet.data?.balance !== undefined && (
                <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-1.5 text-primary font-medium">
                  <Wallet className="h-4 w-4" />
                  <span>Wallet Balance:</span>
                  <strong className="text-base font-bold">{formatInr(wallet.data.balance)}</strong>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {myStore.referralCode && (
                <span className="rounded bg-muted px-2 py-1 font-mono">
                  Referral: <strong className="text-foreground">{myStore.referralCode}</strong>
                </span>
              )}
              <span className="rounded bg-muted px-2 py-1">
                Rating: <strong className="text-foreground">⭐ {stats.rating.toFixed(1)}</strong>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2. DASHBOARD SUMMARY CARDS (KPIs) */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* Total Orders */}
        <Link to="/store/orders" className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Total Orders</span>
                <div className="rounded-lg bg-primary/10 p-2 text-primary group-hover:scale-110 transition-transform">
                  <ShoppingCart className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalAllOrders.toLocaleString()}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {stats.totalOrders} Retail • {totalSupplyOrders} Supply
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Supply Orders (Purchases from Wholesaler) */}
        <Link to="/store/supply/orders" className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Supply Orders</span>
                <div className="rounded-lg bg-purple-500/10 p-2 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
                  <Boxes className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalSupplyOrders}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {pendingSupplyCount > 0 ? (
                    <span className="font-semibold text-amber-600 dark:text-amber-400">
                      {pendingSupplyCount} pending with wholesaler
                    </span>
                  ) : (
                    "All received"
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Available Stock */}
        <Link to="/store/products" className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Available Stock</span>
                <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                  <Package className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {stats.totalProducts} Items
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {totalChannelUnits} supply units on hand
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Pending Orders (Needs Attention) */}
        <Link to="/store/orders" className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-amber-500/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Pending Orders</span>
                <div className="rounded-lg bg-amber-500/10 p-2 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">
                  <Clock className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalPendingOrders}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {activeCustomerOrders.length} customer • {pendingSupplyCount} supply
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Completed Orders */}
        <Link to="/store/orders" className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-emerald-500/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Completed Orders</span>
                <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalCompletedOrders}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {deliveredCustomerOrders.length} customer delivered
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Today's Revenue */}
        <Link to="/store/orders" className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Today's Revenue</span>
                <div className="rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 p-2 text-primary group-hover:scale-110 transition-transform">
                  <IndianRupee className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {formatInr(stats.todayRevenue)}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {stats.todayOrders} orders billed today
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Secondary Financial & Inventory Highlights */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total Customer Revenue</p>
              <p className="text-lg font-bold text-foreground mt-0.5">{formatInr(stats.totalRevenue)}</p>
              <p className="text-[11px] text-muted-foreground">Cumulative customer billing</p>
            </div>
            <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
              <TrendingUp className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Store Earnings</p>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                {formatInr(stats.totalEarnings)}
              </p>
              <p className="text-[11px] text-muted-foreground">Net store profits earned</p>
            </div>
            <div className="rounded-xl bg-emerald-500/10 p-2.5 text-emerald-600 dark:text-emerald-400">
              <Coins className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Pending Payout</p>
              <p className="text-lg font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                {formatInr(stats.pendingSettlement)}
              </p>
              <p className="text-[11px] text-muted-foreground">Scheduled for wallet settlement</p>
            </div>
            <div className="rounded-xl bg-amber-500/10 p-2.5 text-amber-600 dark:text-amber-400">
              <Clock className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Channel Stock on Shelf</p>
              <p className="text-lg font-bold text-foreground mt-0.5">{totalChannelUnits} Units</p>
              <Link
                to="/store/supply/stock"
                className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5 mt-0.5"
              >
                View Channel Stock <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="rounded-xl bg-purple-500/10 p-2.5 text-purple-600 dark:text-purple-400">
              <Boxes className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 4. RECENT ORDERS SECTION */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3 border-b border-border/40">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-bold tracking-tight">Recent Orders</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Latest customer retail purchases and wholesale supply inventory orders.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild className="gap-1.5 text-xs">
                <Link to="/store/orders">
                  Customer Orders
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild className="gap-1.5 text-xs">
                <Link to="/store/supply/orders">
                  Supply Orders
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <Tabs defaultValue="customer" className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <TabsList className="grid w-full sm:w-auto grid-cols-3">
                <TabsTrigger value="customer" className="text-xs sm:text-sm">
                  Customer Orders ({customerOrders.length})
                </TabsTrigger>
                <TabsTrigger value="supply" className="text-xs sm:text-sm">
                  Supply Purchases ({supplyOrdersList.length})
                </TabsTrigger>
                <TabsTrigger value="low-stock" className="text-xs sm:text-sm">
                  Low Stock Alerts ({lowStockProducts.length})
                </TabsTrigger>
              </TabsList>
            </div>

            {/* TAB 1: CUSTOMER RETAIL ORDERS */}
            <TabsContent value="customer" className="m-0 space-y-4">
              {customerOrders.length === 0 ? (
                <div className="py-12 text-center">
                  <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-2 text-sm font-medium text-foreground">No customer orders yet</p>
                  <p className="text-xs text-muted-foreground">
                    Orders placed by customers through the storefront or POS billing will appear here.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Order ID</th>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3 text-center">Items</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {customerOrders.slice(0, 7).map((order) => (
                        <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-mono font-semibold text-primary">
                            {order.orderId}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-foreground">{order.customerName}</p>
                              {order.customerPhone && (
                                <p className="text-xs text-muted-foreground">{order.customerPhone}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-medium">
                            {order.itemCount} items
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-foreground">
                            {formatInr(order.total)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {order.orderDate}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            <Badge
                              variant="outline"
                              className={`shrink-0 capitalize ${customerStatusBadgeColor[order.status] || ""}`}
                            >
                              {order.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-xs"
                              onClick={() => setSelectedCustomerOrderId(Number(order.id))}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* TAB 2: STORE SUPPLY ORDERS (PURCHASES FROM WHOLESALER) */}
            <TabsContent value="supply" className="m-0 space-y-4">
              {supplyOrdersList.length === 0 ? (
                <div className="py-12 text-center">
                  <Boxes className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-2 text-sm font-medium text-foreground">No supply orders placed</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Order stock from your wholesale supplier to replenish your store inventory.
                  </p>
                  <Button asChild size="sm">
                    <Link to="/store/supply">Order Store Supply</Link>
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Order ID</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Supplier (Wholesaler)</th>
                        <th className="px-4 py-3 text-center">Qty</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {supplyOrdersList.slice(0, 7).map((order) => (
                        <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-mono font-semibold text-primary">
                            #{order.id}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <StandardImage
                                src={order.productImageUrl}
                                alt={order.productName}
                                className="h-9 w-9 shrink-0 rounded-md border border-border object-cover"
                              />
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate max-w-[180px] sm:max-w-[220px]">
                                  {order.productName}
                                </p>
                                {order.productSku && (
                                  <p className="text-[11px] text-muted-foreground font-mono">
                                    SKU: {order.productSku}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-foreground">{order.sellerName || "Wholesaler"}</p>
                              <p className="text-xs text-muted-foreground">{order.sellerLevel || "Channel Partner"}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-medium">
                            {order.quantity}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-foreground">
                            {formatInr(order.totalAmount || order.quantity * order.unitPrice)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(order.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {getChannelStatusBadge(order.statusText)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-xs"
                              onClick={() => setSelectedSupplyOrder(order)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* TAB 3: LOW STOCK ALERTS */}
            <TabsContent value="low-stock" className="m-0 space-y-4">
              {lowStockProducts.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500/60" />
                  <p className="mt-2 text-sm font-medium text-foreground">All store products well stocked</p>
                  <p className="text-xs text-muted-foreground">
                    No products currently under their low stock replenishment threshold.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3 text-center">Stock Level</th>
                        <th className="px-4 py-3 text-right">Price</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {lowStockProducts.slice(0, 7).map((product) => (
                        <tr key={product.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <img
                                src={product.image}
                                alt={product.name}
                                className="h-9 w-9 rounded-md border border-border object-cover"
                              />
                              <div>
                                <p className="font-medium text-foreground truncate max-w-[200px]">
                                  {product.name}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {product.category || "General"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge
                              variant="outline"
                              className={
                                (product.stock ?? 0) === 0
                                  ? "bg-destructive/10 text-destructive border-destructive/20 font-bold"
                                  : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 font-bold"
                              }
                            >
                              {(product.stock ?? 0) === 0 ? "Out of Stock" : `${product.stock} Left`}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {formatInr(product.price)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline" className="text-xs">
                              {product.status || "Active"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button asChild size="sm" variant="default" className="h-8 gap-1 text-xs">
                              <Link to="/store/supply">
                                <PlusCircle className="h-3.5 w-3.5" />
                                Order Supply
                              </Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* CUSTOMER ORDER DETAIL DIALOG */}
      <Dialog
        open={selectedCustomerOrderId !== null}
        onOpenChange={(open) => !open && setSelectedCustomerOrderId(null)}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-center justify-between pr-4">
              <DialogTitle className="text-lg">
                Order #{selectedCustomerOrder?.orderNumber || selectedCustomerOrder?.id}
              </DialogTitle>
              {selectedCustomerOrder && (
                <Badge
                  variant="outline"
                  className={customerStatusBadgeColor[mapOrderStatusToStoreUi(selectedCustomerOrder.status)]}
                >
                  {mapOrderStatusToStoreUi(selectedCustomerOrder.status)}
                </Badge>
              )}
            </div>
            <DialogDescription>
              Customer order details and item breakdown.
            </DialogDescription>
          </DialogHeader>

          {customerOrderDetailLoading && (
            <div className="space-y-3 py-6">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {!customerOrderDetailLoading && selectedCustomerOrder && (
            <div className="space-y-4 py-2 text-sm max-h-[70vh] overflow-y-auto pr-1">
              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-3 bg-muted/40 p-3 rounded-lg text-xs">
                <div>
                  <span className="text-muted-foreground block">Date & Time</span>
                  <span className="font-medium text-foreground">
                    {formatDateTime(selectedCustomerOrder.createdAt)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Payment Status</span>
                  <span className="font-medium text-foreground">
                    {selectedCustomerOrder.paymentStatusText}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Payment Method</span>
                  <span className="font-medium text-foreground">
                    {selectedCustomerOrder.paymentMethodText}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Customer Phone</span>
                  <span className="font-medium text-foreground">
                    {selectedCustomerOrder.customerPhone || "—"}
                  </span>
                </div>
              </div>

              {/* Customer and Shipping Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="border border-border p-3 rounded-lg">
                  <h4 className="font-semibold mb-1 text-foreground">Customer Info</h4>
                  <p className="font-medium">{selectedCustomerOrder.customerName}</p>
                  <p className="text-muted-foreground">{selectedCustomerOrder.customerPhone}</p>
                  {selectedCustomerOrder.customerNotes && (
                    <p className="mt-2 text-[11px] bg-amber-500/10 text-amber-700 dark:text-amber-400 p-1.5 rounded">
                      <strong>Note:</strong> {selectedCustomerOrder.customerNotes}
                    </p>
                  )}
                </div>
                <div className="border border-border p-3 rounded-lg">
                  <h4 className="font-semibold mb-1 text-foreground">Delivery Address</h4>
                  <p className="font-medium">{selectedCustomerOrder.shippingAddress?.fullName}</p>
                  <p className="text-muted-foreground">
                    {[
                      selectedCustomerOrder.shippingAddress?.addressLine1,
                      selectedCustomerOrder.shippingAddress?.city,
                      selectedCustomerOrder.shippingAddress?.state,
                      selectedCustomerOrder.shippingAddress?.pincode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-xs text-left">
                  <thead className="bg-muted text-muted-foreground uppercase font-semibold">
                    <tr>
                      <th className="p-2.5">Item</th>
                      <th className="p-2.5 text-center">Qty</th>
                      <th className="p-2.5 text-right">Price</th>
                      <th className="p-2.5 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {selectedCustomerOrder.items.map((item) => (
                      <tr key={item.id} className="hover:bg-muted/30">
                        <td className="p-2.5 font-medium">{item.productName}</td>
                        <td className="p-2.5 text-center">{item.quantity}</td>
                        <td className="p-2.5 text-right">{formatInr(item.unitPrice)}</td>
                        <td className="p-2.5 text-right font-semibold">{formatInr(item.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Order Financials */}
              <div className="rounded-lg bg-muted/40 p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items Subtotal:</span>
                  <span>{formatInr(selectedCustomerOrder.subTotal)}</span>
                </div>
                {selectedCustomerOrder.deliveryCharge > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery Charge:</span>
                    <span>{formatInr(selectedCustomerOrder.deliveryCharge)}</span>
                  </div>
                )}
                {selectedCustomerOrder.taxAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax / GST:</span>
                    <span>{formatInr(selectedCustomerOrder.taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-sm pt-1 border-t border-border/50 text-foreground">
                  <span>Grand Total:</span>
                  <span>{formatInr(selectedCustomerOrder.totalAmount)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex sm:justify-between items-center gap-2">
            <Button variant="outline" onClick={() => setSelectedCustomerOrderId(null)}>
              Close
            </Button>
            <Button asChild>
              <Link to="/store/orders">
                Manage in Orders Panel
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SUPPLY ORDER DETAIL DIALOG */}
      <Dialog
        open={selectedSupplyOrder !== null}
        onOpenChange={(open) => !open && setSelectedSupplyOrder(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between pr-4">
              <DialogTitle className="text-lg">Supply Order #{selectedSupplyOrder?.id}</DialogTitle>
              {selectedSupplyOrder && getChannelStatusBadge(selectedSupplyOrder.statusText)}
            </div>
            <DialogDescription>
              Inventory purchase order placed with wholesale partner.
            </DialogDescription>
          </DialogHeader>

          {selectedSupplyOrder && (
            <div className="space-y-4 py-2 text-sm">
              <div className="flex items-center gap-3 rounded-lg border border-border p-3 bg-muted/20">
                <StandardImage
                  src={selectedSupplyOrder.productImageUrl}
                  alt={selectedSupplyOrder.productName}
                  className="h-14 w-14 shrink-0 rounded-lg border border-border object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-foreground truncate">{selectedSupplyOrder.productName}</h4>
                  {selectedSupplyOrder.productSku && (
                    <p className="text-xs text-muted-foreground font-mono">SKU: {selectedSupplyOrder.productSku}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span>
                      Qty: <strong>{selectedSupplyOrder.quantity}</strong>
                    </span>
                    <span>
                      Price: <strong>{formatInr(selectedSupplyOrder.unitPrice)}</strong>
                    </span>
                    <span>
                      BV: <strong>{selectedSupplyOrder.businessVolume ?? 0}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground block font-medium">Supplier (Wholesaler)</span>
                  <p className="font-semibold text-foreground mt-0.5">{selectedSupplyOrder.sellerName || "Wholesaler"}</p>
                  <p className="text-muted-foreground">{selectedSupplyOrder.sellerLevel || "Wholesale Hub"}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground block font-medium">Purchasing Store</span>
                  <p className="font-semibold text-foreground mt-0.5">{storeName}</p>
                  <p className="text-muted-foreground font-mono">{storeUid}</p>
                </div>
              </div>

              <div className="rounded-lg bg-muted/40 p-3 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order Date:</span>
                  <span>{formatDateTime(selectedSupplyOrder.createdAt)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span className="text-muted-foreground">Total Purchase Amount:</span>
                  <span className="text-foreground">
                    {formatInr(selectedSupplyOrder.totalAmount || selectedSupplyOrder.quantity * selectedSupplyOrder.unitPrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status:</span>
                  <span>{selectedSupplyOrder.paymentStatusText || "Pending"}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex sm:justify-between items-center gap-2">
            <Button variant="outline" onClick={() => setSelectedSupplyOrder(null)}>
              Close
            </Button>
            <Button asChild>
              <Link to="/store/supply/orders">
                View in Supply Orders
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreDashboard;
